import * as os from 'node:os';
import * as utils from '../utils';
import * as mockNetworkRuleFileSchema from './mockNetworkRuleFileSchema.json';
import * as prepareRunnerAndTestimStartUtils from './prepareRunnerAndTestimStartUtils';
import Ajv from 'ajv';
import { CLI_MODE } from './constants';
import { downloadAndInstallChromium } from '../chromiumInstaller';
import { getLogger } from './logger';
import type { RunnerOptions } from '../runOptions';

const MAX_RULE_FILE_SIZE_IN_MB = 1;
const PREPARE_MOCK_NETWORK_ERROR_PREFIX = 'JSON file supplied to --mock-network-pattern';

const logger = getLogger('prepare runner');

export async function prepare(options: RunnerOptions) {
    let chromeDriverPromise = Promise.resolve();

    if (options.useLocalChromeDriver) {
        options.chromeBinaryLocation = options.downloadBrowser ? await downloadAndInstallChromium() : options.chromeBinaryLocation;
        chromeDriverPromise = prepareRunnerAndTestimStartUtils.prepareChromeDriver(
            { projectId: options.project, userId: options.user },
            { chromeBinaryLocation: options.chromeBinaryLocation },
            Boolean(options.lightweightMode?.general),
        );
        options.useLocalChromeDriver = true;
    }

    if (!options.playerRequirePath && options.mode !== CLI_MODE.EXTENSION) {
        await prepareRunnerAndTestimStartUtils.preparePlayer(options.playerLocation, options.canary);
    }
    // Unless the user specified different mode than Extension/passed ext as a flag, we want to prepare the extension.
    if (options.mode !== CLI_MODE.SELENIUM && options.mode !== CLI_MODE.APPIUM && !options.ext) {
        await prepareRunnerAndTestimStartUtils.prepareExtension(options.extensionLocation);
    }

    let customExtensionLocation;

    if (options.installCustomExtension) {
        const unlimitedSize = Boolean(options.useLocalChromeDriver || options.useChromeLauncher);
        customExtensionLocation = await prepareRunnerAndTestimStartUtils.prepareCustomExtension(options.installCustomExtension, unlimitedSize);
    }

    await chromeDriverPromise;

    return customExtensionLocation;
}

export async function prepareMockNetwork(location: string) {
    logger.info('prepare MockNetwork', { location });
    const _rulesJson = await utils.getSourceAsBuffer(location);
    const rulesJsonBuf = Buffer.isBuffer(_rulesJson) ? _rulesJson : _rulesJson.body;
    if (rulesJsonBuf.byteLength > MAX_RULE_FILE_SIZE_IN_MB * 1_000_000) {
        throw new Error(`${PREPARE_MOCK_NETWORK_ERROR_PREFIX} exceeded ${MAX_RULE_FILE_SIZE_IN_MB}MB`);
    }

    let rulesJson;
    try {
        rulesJson = JSON.parse(rulesJsonBuf.toString('utf-8'));
    } catch (error) {
        throw new Error(`${PREPARE_MOCK_NETWORK_ERROR_PREFIX} cannot be parsed.${os.EOL}${error}`);
    }

    const ajv = new Ajv();
    const valid = ajv.validate(mockNetworkRuleFileSchema, rulesJson);
    if (!valid) {
        const errors = ajv.errors?.map((e, i) => `${++i}) ${e.dataPath} ${e.message}`).join(os.EOL);
        throw new Error(`${PREPARE_MOCK_NETWORK_ERROR_PREFIX} is malformed.${os.EOL}${errors}`);
    }

    return rulesJson.entries;
}

export { initializeUserWithAuth } from './initializeUserWithAuth';
